import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { ConfirmationDialog } from '@amore/ui/components/confirmation-dialog'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@amore/ui/components/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { type ReactNode, useEffect, useState } from 'react'

export type DialogAction = {
  label: string
  onClick: () => void
  disabled?: boolean
  /** Renders the action as this icon alone; `label` becomes its tooltip and accessible name. */
  icon?: ReactNode
}

type SurfaceProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children: ReactNode
  /** The one primary action, bottom right. Omit it for read-only content. */
  primaryAction?: DialogAction
  /** An optional secondary action, left of the primary. */
  secondaryAction?: DialogAction
  /** A custom control left of the primary, e.g. the task's done toggle. */
  footerAccessory?: ReactNode
  /** An optional destructive action, bottom left (e.g. "Smazat úkol"). */
  destructiveAction?: DialogAction
  /** With unsaved input, closing asks before discarding it. */
  dirty?: boolean
  /** Replaces the visible title, e.g. with an in-place editable heading. `title` stays for screen readers. */
  titleContent?: ReactNode
}

const exitMs = 200
const overlayMotion =
  'duration-200 motion-safe:data-[state=closed]:animate-out motion-safe:data-[state=closed]:fade-out-0'
const sheetMotion =
  'duration-200 ease-out motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:slide-in-from-right motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=closed]:animate-out motion-safe:data-[state=closed]:slide-out-to-right motion-safe:data-[state=closed]:fade-out-0'
const dialogMotion =
  'duration-200 motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=open]:zoom-in-95 motion-safe:data-[state=closed]:animate-out motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=closed]:zoom-out-95'

/**
 * Short create/edit flows. Closes only through the × in the corner: there is
 * never a "Zavřít" or "Zrušit" button.
 */
export function FormDialog({
  width = 'regular',
  ...props
}: SurfaceProps & { width?: 'compact' | 'regular' | 'wide' }) {
  return <Surface {...props} variant={{ type: 'dialog', width }} />
}

/**
 * Record detail (a task, a party, a supplier, an expense): a sheet from the
 * right edge, so the list stays visible. Same header, × and footer rules.
 * `aside` makes it a two-part sheet (a supplier): the aside fills the left
 * part with its own scroll, the usual edit column stays on the right. Below
 * `lg` the aside is hidden; the screen shows it inside the edit column.
 */
export function DetailSheet({
  eyebrow,
  width = 'regular',
  aside,
  ...props
}: SurfaceProps & { eyebrow?: string; width?: 'regular' | 'wide'; aside?: ReactNode }) {
  return <Surface {...props} eyebrow={eyebrow} aside={aside} variant={{ type: 'sheet', width }} />
}

function Surface({
  open,
  onOpenChange,
  title,
  eyebrow,
  description,
  children,
  primaryAction,
  secondaryAction,
  destructiveAction,
  dirty = false,
  titleContent,
  footerAccessory,
  variant,
  aside,
}: SurfaceProps & {
  eyebrow?: string
  aside?: ReactNode
  variant:
    | { type: 'dialog'; width: 'compact' | 'regular' | 'wide' }
    | { type: 'sheet'; width: 'regular' | 'wide' }
}) {
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  // Screens often unmount the surface as soon as it closes; holding `onOpenChange(false)`
  // until the exit animation ends lets the sheet slide out and the overlay fade.
  const [shown, setShown] = useState(open)
  useEffect(() => {
    if (open) setShown(true)
  }, [open])
  const close = () => {
    setShown(false)
    window.setTimeout(() => onOpenChange(false), exitMs)
  }
  const requestClose = (next: boolean) => {
    if (next) return onOpenChange(true)
    if (dirty) return setConfirmDiscard(true)
    close()
  }
  const sheet = variant.type === 'sheet'
  return (
    <>
      <Dialog open={open && shown} onOpenChange={requestClose}>
        <DialogContent
          closeLabel="Zavřít"
          width={variant.type === 'dialog' ? variant.width : 'wide'}
          overlayClassName={overlayMotion}
          // A sheet opens to read, not to type: focus only a field marked `data-autofocus`
          // (e.g. a new record's name), never just the first field.
          onOpenAutoFocus={
            sheet
              ? (event) => {
                  event.preventDefault()
                  const target = (
                    event.currentTarget as HTMLElement | null
                  )?.querySelector<HTMLElement>('[data-autofocus]')
                  target?.focus({ preventScroll: true })
                }
              : undefined
          }
          className={cn(
            sheet
              ? [
                  aside
                    ? 'w-[min(100vw,560px)] lg:w-[min(calc(100vw-1rem),1240px)] lg:flex-row'
                    : variant.width === 'wide'
                      ? 'w-[min(100vw,880px)] sm:w-[min(calc(100vw-1rem),880px)]'
                      : 'w-[min(100vw,560px)]',
                  'top-0 right-0 left-auto h-dvh max-h-dvh max-w-none translate-x-0 translate-y-0 rounded-none sm:top-2 sm:right-2 sm:h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-1rem)] sm:rounded-panel',
                  sheetMotion,
                ]
              : dialogMotion,
          )}
        >
          {aside ? (
            <aside className="hidden min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain border-r border-border lg:block">
              {aside}
            </aside>
          ) : null}
          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col',
              aside && 'lg:w-[540px] lg:flex-none',
            )}
          >
            <DialogHeader>
              {eyebrow ? (
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-action">
                  {eyebrow}
                </p>
              ) : null}
              <DialogTitle className={titleContent ? 'sr-only' : undefined}>{title}</DialogTitle>
              {titleContent}
              {description ? <DialogDescription>{description}</DialogDescription> : null}
            </DialogHeader>
            <DialogBody>{children}</DialogBody>
            {primaryAction || secondaryAction || destructiveAction || footerAccessory ? (
              <DialogFooter className="flex items-center gap-3">
                {destructiveAction?.icon ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        appearance="ghost"
                        tone="danger"
                        size="icon"
                        aria-label={destructiveAction.label}
                        onClick={destructiveAction.onClick}
                        disabled={destructiveAction.disabled}
                      >
                        {destructiveAction.icon}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{destructiveAction.label}</TooltipContent>
                  </Tooltip>
                ) : destructiveAction ? (
                  <Button
                    appearance="ghost"
                    tone="danger"
                    onClick={destructiveAction.onClick}
                    disabled={destructiveAction.disabled}
                  >
                    {destructiveAction.label}
                  </Button>
                ) : null}
                <span className="flex-1" />
                {footerAccessory}
                {secondaryAction ? (
                  <Button
                    appearance="outline"
                    tone="neutral"
                    onClick={secondaryAction.onClick}
                    disabled={secondaryAction.disabled}
                  >
                    {secondaryAction.label}
                  </Button>
                ) : null}
                {primaryAction ? (
                  <Button onClick={primaryAction.onClick} disabled={primaryAction.disabled}>
                    {primaryAction.label}
                  </Button>
                ) : null}
              </DialogFooter>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        heading="Zahodit rozepsané změny?"
        description="Co jste vyplnili, se neuloží."
        cancelLabel="Pokračovat v úpravách"
        confirmLabel="Zahodit"
        onConfirm={() => {
          setConfirmDiscard(false)
          close()
        }}
      />
    </>
  )
}

/** Confirms a destructive action. The only dialog with two buttons. */
export function ConfirmDialog({
  open,
  onOpenChange,
  heading,
  description,
  confirmLabel,
  keepLabel,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  heading: string
  description: ReactNode
  confirmLabel: string
  keepLabel: string
  onConfirm: () => void
}) {
  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      heading={heading}
      description={description}
      cancelLabel={keepLabel}
      confirmLabel={confirmLabel}
      onConfirm={() => {
        onConfirm()
        onOpenChange(false)
      }}
    />
  )
}
